import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PATH_SEGMENTS, route } from '../config/paths';
import {
  createPublicDemoSession,
  demoTokenStorageKey,
  getPublicDemo,
  DemoCatalogEntry,
} from '../services/publicDemosClient';
import LiveDemoShell from '../components/landing/LiveDemoShell';
import '../styles/screens/LiveDemo.css';

const LiveDemoDetail: React.FC = () => {
  const { demoId = '' } = useParams();
  const navigate = useNavigate();
  const [demo, setDemo] = useState<DemoCatalogEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const row = await getPublicDemo(demoId);
        if (!cancelled) setDemo(row);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Demo not found');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [demoId]);

  const handleStart = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await createPublicDemoSession(demoId);
      sessionStorage.setItem(demoTokenStorageKey(data.session.session_id), data.access_token);
      sessionStorage.setItem(
        `demo_bootstrap:${data.session.session_id}`,
        JSON.stringify({ turn: data.turn, result: data.result, demo_id: data.demo_id })
      );
      navigate(route(PATH_SEGMENTS.LIVE_DEMO, demoId, PATH_SEGMENTS.LIVE_DEMO_PLAY), {
        state: { sessionId: data.session.session_id },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start demo');
    } finally {
      setBusy(false);
    }
  };

  if (!demo && !error) {
    return (
      <LiveDemoShell>
        <div className="live-demo live-demo-muted">Loading…</div>
      </LiveDemoShell>
    );
  }

  const agentName = demo?.agent?.display_name || 'the agent';

  return (
    <LiveDemoShell>
      <div className="live-demo live-demo-detail">
        <Link className="live-demo-back" to={route(PATH_SEGMENTS.LIVE_DEMO)}>
          ← All demos
        </Link>
        {error && <div className="live-demo-error">{error}</div>}
        {demo && (
          <div className="live-demo-detail-layout">
            <div className="live-demo-detail-main">
              <div className="live-demo-detail-kicker">
                {demo.industry} · {demo.country}
                {demo.languages?.length ? ` · ${demo.languages.join(', ')}` : ''}
              </div>
              <h1>{demo.title}</h1>
              <p className="live-demo-detail-tagline">
                {demo.agent?.tagline || demo.summary}
              </p>
              <h3 className="live-demo-detail-steps-title">Agent workflow</h3>
              <ol className="live-demo-detail-steps">
                {demo.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>

            <aside className="live-demo-detail-panel">
              <div className="live-demo-detail-stage" aria-hidden="true">
                <div className="live-demo-detail-orb">
                  <span className="live-demo-detail-mic" />
                  <div className="live-demo-detail-rings">
                    <span />
                    <span />
                  </div>
                </div>
                <div className="live-demo-detail-wave">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <span key={i} style={{ animationDelay: `${i * 0.07}s` }} />
                  ))}
                </div>
                <p className="live-demo-detail-agent-name">{agentName}</p>
              </div>
              <p className="live-demo-mic-note">
                Your browser will ask for microphone access so you can speak with the agent. Audio
                stays in this demo session.
              </p>
              <button
                className="live-demo-start"
                type="button"
                disabled={busy}
                onClick={handleStart}
              >
                <span className="live-demo-start-dot" aria-hidden="true" />
                {busy ? 'Starting…' : `Talk to ${agentName}`}
              </button>
            </aside>
          </div>
        )}
      </div>
    </LiveDemoShell>
  );
};

export default LiveDemoDetail;
